import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Space,
  Button,
  Image,
  Modal,
  notification,
} from "antd";
import { Table } from "ant-table-extensions";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";

import Create from "./Create";
import Edit from "./Edit";

import axios from "axios";
import urldata from "../../../../urldata";
import config from "../../../../config/config";

const { confirm } = Modal;

function Index() {
  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });

  const [btnSize, setBtnSize] = useState("middle");
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(1);

  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const moduleName = "Benefit";
  const moduleNamePlural = "Benefits";

  const base_api = urldata + "admin/";
  const getEndpoint = base_api + "getBenefits";
  const azure_base = config.azure_object_url || "";

  // ---------------------------
  // Fetch Data
  // ---------------------------
  const fetchDatas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(getEndpoint);

      const allParents = response?.data?.data || [];

      // Flatten all parents + their benefits
      const formatted = allParents.flatMap((parent) =>
        parent.benefits.map((b) => ({
          ...b,
          parentId: parent._id, // include parent id
        }))
      );

      setDatas(formatted);

      if (formatted.length === 0) {
        notification.info({ message: "No benefits found" });
      }
    } catch (err) {
      console.error(err);
      notification.error({ message: "Failed to fetch data" });
      setDatas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatas();
  }, []);

  useEffect(() => {
    setBtnSize(isMobile ? "small" : "middle");
  }, [isMobile]);

  // ---------------------------
  // Create Handlers
  // ---------------------------
  const handleCreateOpen = () => setCreateOpen(true);

  const handleCreateClose = () => {
    setCreateKey((k) => k + 1);
    setCreateOpen(false);
    fetchDatas();
  };

  // ---------------------------
  // Edit Handlers
  // ---------------------------
  const handleEditOpen = (record) => {
    setSelectedRecord(record);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditKey((k) => k + 1);
    setSelectedRecord(null);
    setEditOpen(false);
    fetchDatas();
  };

  // ---------------------------
  // Delete Handler
  // ---------------------------
  const handleDelete = (parentId, benefitId) => {
    confirm({
      title: `Are you sure you want to delete this ${moduleName}?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        try {
          const endpoint = base_api + "deleteBenefits"; // <-- note singular

          await axios.post(endpoint, { parentId, benefitId });

          notification.success({
            message: `${moduleName} deleted successfully`,
          });

          fetchDatas();
        } catch (err) {
          console.error(err);
          notification.error({ message: "Failed to delete" });
        }
      },
    });
  };

  // ---------------------------
  // Table Columns
  // ---------------------------
  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Icon",
      key: "iconImg",
      render: (_, record) => (
        <Image
          width={70}
          src={azure_base + record.iconImg}
          fallback="https://via.placeholder.com/70"
        />
      ),
    },
    {
      title: "Desktop Image",
      key: "webImg",
      render: (_, record) => (
        <Image
          width={100}
          src={azure_base + record.webImg}
          fallback="https://via.placeholder.com/100"
        />
      ),
    },

    {
      title: "Mobile Image",
      key: "mobileImg",
      render: (_, record) => (
        <Image
          width={100}
          src={azure_base + record.mobileImg}
          fallback="https://via.placeholder.com/100"
        />
      ),
    },

    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            size={btnSize}
            onClick={() => handleEditOpen(record)}
          >
            Edit
          </Button>

          {/* <Button
            type="danger"
            size={btnSize}
            onClick={() => handleDelete(record.parentId, record._id)}
          >
            Delete
          </Button> */}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>{moduleNamePlural}</Breadcrumb.Item>
      </Breadcrumb>

      <Row>
        <Col span={24}>
          <Card title={moduleNamePlural} bordered={false}>
            <Row style={{ marginBottom: 20 }}>
              <Col span={24}>
                <Space>
                  {datas.length < 5 && (
                    <Button
                      type="primary"
                      size={btnSize}
                      onClick={handleCreateOpen}
                    >
                      Add {moduleName}
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>

            <Table
              loading={loading}
              rowKey={(record) => record._id}
              columns={columns}
              dataSource={datas}
              footer={() => `Total ${moduleNamePlural} = ${datas.length}`}
            />

            {/* CREATE */}
            <Create
              key={"create" + createKey}
              isVisible={createOpen}
              handleClose={handleCreateClose}
              moduleName={moduleName}
              base_url={base_api}
              existingCount={datas.length}
            />

            {/* EDIT */}
            {selectedRecord && (
              <Edit
                key={"edit" + editKey}
                isVisible={editOpen}
                handleClose={handleEditClose}
                moduleName={moduleName}
                base_url={base_api}
                record={selectedRecord}
                azure_base={azure_base}
                recordIndex={datas.findIndex((d) => d._id === selectedRecord._id)}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Index;
